import { formatTermsDate } from "./termsFormat";
import { ensureGeneralSansFonts, GENERAL_SANS } from "./termsPdfFonts";
import { buildTermsPdfFilename } from "./termsPdfFilename";
import {
	cmsHtmlToPdfBlocks,
	iPdfBlock,
	iPdfTableRow,
	iPdfTextRun,
	PdfFontStyle,
} from "./termsPdfBlocks";

export interface iTermsPdfSource {
	title: string;
	version: string;
	effectiveDate?: string;
	/** Sanitized CMS HTML fragment from `GET …/terms/:version/content`. */
	html: string;
}

export { buildTermsPdfFilename } from "./termsPdfFilename";
export { cmsHtmlToPdfBlocks } from "./termsPdfBlocks";

const PAGE = {
	marginMm: 16,
	titleSize: 16,
	metaSize: 10,
	headingSizes: { 1: 12, 2: 11.5, 3: 11, 4: 11 } as Record<number, number>,
	bodySize: 10,
	lineHeight: 1.5,
	blockGapMm: 2.5,
	headingBeforeMm: 4,
	headingAfterMm: 2.5,
	/** One line of space between document title and version subtitle. */
	titleToMetaMm: 7,
	metaToBorderMm: 2,
	tableCellPadMm: 2.5,
	tableBorderMm: 0.2,
	/**
	 * Extra blank-line multipliers after a block's last baseline. Content-agnostic:
	 * driven by block kind / leading emphasis, not by specific CMS copy.
	 */
	blankLinesAfterHeaderBorder: 1.5,
	blankLinesAfterBody: 1,
	blankLinesAfterHeading: 0.75,
	blankLinesAfterTable: 1.5,
	blankLinesBeforeSubheading: 0.75,
};

const ptToMm = (pt: number): number => pt * 0.352778;

const ensureSpace = (
	doc: import("jspdf").jsPDF,
	y: number,
	neededMm: number,
	pageHeight: number,
	margin: number
): number => {
	if (y + neededMm <= pageHeight - margin) {
		return y;
	}

	doc.addPage();
	return margin;
};

const setFont = (doc: import("jspdf").jsPDF, style: PdfFontStyle) => {
	doc.setFont(GENERAL_SANS, style);
};

const isEmphasizedStyle = (style: PdfFontStyle): boolean =>
	style === "medium" || style === "mediumitalic";

/**
 * Draws wrapped runs. Returns the baseline of the last line drawn (no trailing
 * blank line) so callers control inter-block spacing explicitly.
 */
const drawRuns = (
	doc: import("jspdf").jsPDF,
	runs: iPdfTextRun[],
	x: number,
	y: number,
	maxWidth: number,
	fontSize: number,
	pageHeight: number,
	margin: number,
	lineHeight = PAGE.lineHeight
): number => {
	doc.setFontSize(fontSize);
	const lineHeightMm = ptToMm(fontSize * lineHeight);

	let cursorX = x;
	let cursorY = y;
	const lineStartX = x;

	const drawWord = (word: string, style: PdfFontStyle) => {
		setFont(doc, style);
		const wordWidth = doc.getTextWidth(word);
		const spaceWidth = doc.getTextWidth(" ");

		if (cursorX > lineStartX && cursorX + wordWidth > lineStartX + maxWidth) {
			cursorY = ensureSpace(doc, cursorY + lineHeightMm, lineHeightMm, pageHeight, margin);
			cursorX = lineStartX;
		}

		if (cursorX === lineStartX && wordWidth > maxWidth) {
			const chars = word.split("");
			let chunk = "";
			chars.forEach((char) => {
				const next = chunk + char;
				if (doc.getTextWidth(next) > maxWidth && chunk) {
					doc.text(chunk, cursorX, cursorY);
					cursorY = ensureSpace(doc, cursorY + lineHeightMm, lineHeightMm, pageHeight, margin);
					cursorX = lineStartX;
					chunk = char;
				} else {
					chunk = next;
				}
			});
			if (chunk) {
				doc.text(chunk, cursorX, cursorY);
				cursorX += doc.getTextWidth(chunk);
			}
			return;
		}

		doc.text(word, cursorX, cursorY);
		cursorX += wordWidth + spaceWidth;
	};

	runs.forEach((run) => {
		const parts = run.text.split("\n");
		parts.forEach((part, partIndex) => {
			if (partIndex > 0) {
				cursorY = ensureSpace(doc, cursorY + lineHeightMm, lineHeightMm, pageHeight, margin);
				cursorX = lineStartX;
			}

			const words = part.split(/\s+/).filter(Boolean);
			words.forEach((word) => drawWord(word, run.style));
		});
	});

	return cursorY;
};

const measureRunsHeight = (
	doc: import("jspdf").jsPDF,
	runs: iPdfTextRun[],
	maxWidth: number,
	fontSize: number,
	lineHeight = PAGE.lineHeight
): number => {
	doc.setFontSize(fontSize);
	const lineHeightMm = ptToMm(fontSize * lineHeight);
	let lines = 1;
	let lineWidth = 0;

	runs.forEach((run) => {
		setFont(doc, run.style);
		const words = run.text.split(/\s+/).filter(Boolean);
		words.forEach((word) => {
			const wordWidth = doc.getTextWidth(word);
			const spaceWidth = doc.getTextWidth(" ");
			if (lineWidth > 0 && lineWidth + wordWidth > maxWidth) {
				lines += 1;
				lineWidth = wordWidth + spaceWidth;
			} else {
				lineWidth += wordWidth + spaceWidth;
			}
		});
	});

	return Math.max(lineHeightMm, lines * lineHeightMm);
};

const drawTable = (
	doc: import("jspdf").jsPDF,
	rows: iPdfTableRow[],
	x: number,
	y: number,
	width: number,
	pageHeight: number,
	margin: number
): number => {
	const colCount = Math.max(...rows.map((row) => row.cells.length), 1);
	const colWidth = width / colCount;
	const pad = PAGE.tableCellPadMm;
	const fontSize = PAGE.bodySize;
	const lineHeightMm = ptToMm(fontSize * PAGE.lineHeight);
	let cursorY = y;

	doc.setDrawColor(0, 0, 0);
	doc.setLineWidth(PAGE.tableBorderMm);
	doc.setTextColor(0, 0, 0);

	rows.forEach((row) => {
		const cellHeights = row.cells.map((cell) =>
			measureRunsHeight(doc, cell, colWidth - pad * 2, fontSize) + pad * 2
		);
		while (cellHeights.length < colCount) {
			cellHeights.push(pad * 2 + lineHeightMm);
		}
		const rowHeight = Math.max(...cellHeights, pad * 2 + lineHeightMm);

		cursorY = ensureSpace(doc, cursorY, rowHeight, pageHeight, margin);
		const rowTop = cursorY;

		for (let col = 0; col < colCount; col += 1) {
			const cellX = x + col * colWidth;
			doc.rect(cellX, rowTop, colWidth, rowHeight);

			const cellRuns = row.cells[col] ?? [{ text: " ", style: "normal" as const }];
			drawRuns(
				doc,
				cellRuns,
				cellX + pad,
				rowTop + pad + lineHeightMm * 0.75,
				colWidth - pad * 2,
				fontSize,
				pageHeight,
				margin
			);
		}

		cursorY = rowTop + rowHeight;
	});

	return cursorY;
};

const isClauseHeadingListItem = (block: iPdfBlock): boolean => {
	if (block.kind !== "list-item" || block.runs.length < 2) {
		return false;
	}

	const [marker, ...rest] = block.runs;
	if (!/^\d+\.\s*$|^•\s*$/.test(marker.text)) {
		return false;
	}

	return rest.every((run) => isEmphasizedStyle(run.style));
};

const isEmphasisOnlyParagraph = (block: iPdfBlock): boolean =>
	block.kind === "paragraph" &&
	block.runs.length > 0 &&
	block.runs.every((run) => isEmphasizedStyle(run.style));

/**
 * Definition-style leads (`"Account Manager" means…`): first visible run is
 * emphasized. Structural — not tied to specific CMS wording.
 */
const startsWithEmphasis = (block: iPdfBlock): boolean => {
	if (block.kind !== "paragraph" && block.kind !== "list-item") {
		return false;
	}

	const first = block.runs.find((run) => run.text.trim().length > 0);
	return !!first && isEmphasizedStyle(first.style);
};

const isHeadingBlock = (block: iPdfBlock): boolean =>
	block.kind === "heading" || isClauseHeadingListItem(block) || isEmphasisOnlyParagraph(block);

/** Section titles + definition leads that need breathing room between them. */
const isSubheadingBlock = (block: iPdfBlock): boolean =>
	isHeadingBlock(block) || startsWithEmphasis(block);

const bodyLineHeightMm = (): number => ptToMm(PAGE.bodySize * PAGE.lineHeight);

/**
 * Builds a multi-page A4 PDF from the CMS HTML body (not a DOM screenshot of
 * the modal). Header uses the same title / version / effective date the viewer shows.
 */
export const downloadTermsPdf = async (source: iTermsPdfSource): Promise<void> => {
	if (!source.html.trim()) {
		throw new Error("Terms content is empty");
	}

	const blocks = cmsHtmlToPdfBlocks(source.html);
	if (blocks.length === 0) {
		throw new Error("Terms content produced no printable blocks");
	}

	const { jsPDF } = await import("jspdf");
	const filename = buildTermsPdfFilename(source.title, source.version, source.effectiveDate);
	const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
	await ensureGeneralSansFonts(doc);

	const pageWidth = doc.internal.pageSize.getWidth();
	const pageHeight = doc.internal.pageSize.getHeight();
	const margin = PAGE.marginMm;
	const contentWidth = pageWidth - margin * 2;
	const blankLine = bodyLineHeightMm();

	let y = margin;
	doc.setTextColor(0, 0, 0);

	setFont(doc, "medium");
	doc.setFontSize(PAGE.titleSize);
	const titleLines = doc.splitTextToSize(source.title, contentWidth) as string[];
	titleLines.forEach((line: string, index: number) => {
		y = ensureSpace(doc, y, ptToMm(PAGE.titleSize), pageHeight, margin);
		doc.text(line, margin, y);
		if (index < titleLines.length - 1) {
			y += ptToMm(PAGE.titleSize * PAGE.lineHeight);
		}
	});

	const dateLabel = source.effectiveDate ? formatTermsDate(source.effectiveDate) : "";
	const meta = dateLabel
		? `Version ${source.version} · effective ${dateLabel}`
		: `Version ${source.version}`;

	y += PAGE.titleToMetaMm;
	setFont(doc, "normal");
	doc.setFontSize(PAGE.metaSize);
	doc.text(meta, margin, y);

	y += PAGE.metaToBorderMm;
	doc.setDrawColor(0, 0, 0);
	doc.setLineWidth(0.25);
	doc.line(margin, y, pageWidth - margin, y);
	// Blank line(s) under the header rule so the first body heading is never flush.
	y += bodyLineHeightMm() * PAGE.blankLinesAfterHeaderBorder;

	let previousWasSpacer = false;
	let isFirstContentBlock = true;

	blocks.forEach((block) => {
		if (block.kind === "spacer") {
			if (!previousWasSpacer) {
				y += blankLine * PAGE.blankLinesAfterBody;
			}
			previousWasSpacer = true;
			return;
		}
		previousWasSpacer = false;

		if (block.kind === "table" && block.rows?.length) {
			if (!isFirstContentBlock) {
				y += blankLine * PAGE.blankLinesAfterBody;
			}
			isFirstContentBlock = false;
			y = drawTable(doc, block.rows, margin, y, contentWidth, pageHeight, margin);
			// Blank line(s) after every table so following copy never sits on the border.
			y += blankLine * PAGE.blankLinesAfterTable;
			return;
		}

		const indent = block.kind === "list-item" ? Math.min(block.level ?? 0, 4) * 5 : 0;
		const heading = isHeadingBlock(block);
		const subheading = isSubheadingBlock(block);
		const fontSize = heading
			? block.kind === "heading"
				? PAGE.headingSizes[block.level ?? 2] ?? PAGE.bodySize
				: PAGE.headingSizes[3]
			: PAGE.bodySize;
		const lineHeightMm = ptToMm(fontSize * PAGE.lineHeight);
		const runs = block.runs;

		if (!isFirstContentBlock && subheading) {
			// Extra beat before each subheading / definition lead.
			y += blankLine * PAGE.blankLinesBeforeSubheading;
		} else if (!isFirstContentBlock && heading) {
			y += PAGE.headingBeforeMm;
		}

		isFirstContentBlock = false;

		y = ensureSpace(doc, y, lineHeightMm, pageHeight, margin);
		doc.setTextColor(0, 0, 0);
		y = drawRuns(
			doc,
			runs,
			margin + indent,
			y,
			contentWidth - indent,
			fontSize,
			pageHeight,
			margin,
			PAGE.lineHeight
		);

		// Past last baseline + blank line(s). Same rule for any CMS length/order.
		if (heading) {
			y += lineHeightMm * 0.35 + blankLine * PAGE.blankLinesAfterHeading;
		} else {
			y += lineHeightMm + blankLine * PAGE.blankLinesAfterBody;
		}
	});

	doc.save(filename);
};
